import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/nova/components';

import { adSubjectFromFeature, adSubjectFromGame, adSubjectFromItem } from '../../ad-subjects';
import type { AdSubject, AdSubjectType } from '../../banner-kit-api';
import type { CreativeItem } from '../../creative-api';
import type { GameSourceRecord } from '../../game-sources';
import type { GamificationFeature } from '../../gamification-sources';
import { VIDEO_SUBJECT_TYPES, type VideoSubjectType } from '../../video-subjects';
import { GamePickerSheet } from '../GamePickerSheet';
import { FooterActions, FooterRow, SelCount } from '../GamePickerSheet/GamePickerSheet.styles';
import { GamificationPickerSheet } from '../GamificationPickerSheet';
import { PoolPickerSheet } from '../PoolPickerSheet';
import { VideoSubjectPicker } from '../VideoSubjectPicker';

export interface AdSubjectFlowProps {
  /** The brand's promotion pool. */
  items: CreativeItem[];
  itemsLoaded: boolean;
  /** Translated. The promotion picker's title, e.g. "Choose the promotion this image banner advertises". */
  promotionTitle: string;
  /** Translated. The promotion picker's hint (what the pick is used for). */
  promotionHint: string;
  /** The choice is being acted on (the promotion picker's Continue waits). */
  busy?: boolean;
  /** One subject was chosen (a promotion, a game or a gamification feature). */
  onChosen: (type: AdSubjectType, subject: AdSubject) => void;
  /** The categories offered (all of them when omitted), e.g. promotions and games only. */
  subjectTypes?: readonly VideoSubjectType[];
  /** How many games may be picked (1 when omitted). Above 1, the pick goes to `onChosenGames`. */
  gamesMax?: number;
  /** Several games were chosen (only with `gamesMax` above 1). */
  onChosenGames?: (subjects: AdSubject[]) => void;
  /** Dismissed without a choice. */
  onClose: () => void;
}

/**
 * What a new marketing asset advertises: first the category (promotion, game or gamification; `subjectTypes` narrows
 * it), then that category's picker, one item (an ad is one concept) or up to `gamesMax` games (a landing page can
 * feature a few). Mounted for the "create new" step only, so every new asset starts the flow afresh.
 */
export const AdSubjectFlow = ({
  items,
  itemsLoaded,
  promotionTitle,
  promotionHint,
  busy = false,
  onChosen,
  subjectTypes = VIDEO_SUBJECT_TYPES,
  gamesMax = 1,
  onChosenGames,
  onClose,
}: AdSubjectFlowProps): React.ReactElement => {
  const { t } = useTranslation();
  const [subjectType, setSubjectType] = React.useState<AdSubjectType | null>(null);
  const [pickedPromoId, setPickedPromoId] = React.useState<string | null>(null);

  const choosePromotion = (): void => {
    const item = items.find((i) => i.banner_id === pickedPromoId);
    if (item && !busy) onChosen('promotion', adSubjectFromItem(item));
  };
  const chooseGames = (games: GameSourceRecord[]): void => {
    if (gamesMax > 1 && onChosenGames) {
      if (games.length > 0) onChosenGames(games.map((game) => adSubjectFromGame(game, '1:1')));
      return;
    }
    if (games[0]) onChosen('game', adSubjectFromGame(games[0], '1:1'));
  };
  const chooseFeatures = (features: GamificationFeature[]): void => {
    if (features[0]) onChosen('gamification', adSubjectFromFeature(features[0]));
  };

  if (subjectType === null) {
    return <VideoSubjectPicker subjectTypes={subjectTypes} onPick={setSubjectType} onClose={onClose} />;
  }
  if (subjectType === 'game') return <GamePickerSheet max={gamesMax} onConfirm={chooseGames} onClose={onClose} />;
  if (subjectType === 'gamification') {
    return <GamificationPickerSheet max={1} onConfirm={chooseFeatures} onClose={onClose} />;
  }
  return (
    <PoolPickerSheet
      title={promotionTitle}
      poolItems={items}
      selectedIds={pickedPromoId ? [pickedPromoId] : []}
      labelOf={(i) => i.headline || i.internal_name || i.banner_id}
      onToggle={(id) => setPickedPromoId((cur) => (cur === id ? null : id))}
      onClose={onClose}
      max={1}
      emptyText={
        itemsLoaded
          ? t('This brand has no promotions yet. Create one in All Promotions & Creatives first.')
          : t('Loading promotions…')
      }
      hint={promotionHint}
      footer={
        <FooterRow>
          <SelCount text="{{n}} selected" placeholders={{ n: pickedPromoId ? 1 : 0 }} />
          <FooterActions>
            <Button variant="text" label={t('Cancel')} onClick={onClose} />
            <Button
              variant="primary"
              label={t('Continue')}
              onClick={choosePromotion}
              disabled={!pickedPromoId || busy}
            />
          </FooterActions>
        </FooterRow>
      }
    />
  );
};

export default AdSubjectFlow;
